import { Injectable, Injector } from '@angular/core';
import { of, Observable, map, tap } from 'rxjs';
import { CommonQuestionnaireService } from 'src/app/shared/services';
import { BaremosDto, FormDto, RefusalDto } from 'src/app/shared/dtos';
import { FormField } from '@shared/models';
import { RefusalManager } from '@shared/managers';
import { IQuestionnaireStore } from '../store/questionnaire.state';
import { APP_CONFIG, ConfigManager, IAppConfig, CoreExpressions } from '@app/core';
import { BaremosManager } from '../model/baremos.model';
import { Baremos, ResultSimulation} from '@app/questionnaire/interfaces';

/**
 * Questionnaire Service
 */
@Injectable({
	providedIn: 'root'
})
export class QuestionnaireService  {
	/**
	 * Questions array
	 */
	questions: FormField[] = [];

	/**
	 * Application configuration
	 */
	appConfig: IAppConfig;

	/**
	 * Questionnaire home constructor. Sets application configuration
	 *
	 * @param _injector injector
	 * @param _questionnaireService common questionnaire service
	 */
	constructor(
		_injector: Injector,
		private _questionnaireService: CommonQuestionnaireService,
	) {
		this.appConfig = _injector.get<ConfigManager>(APP_CONFIG).config;
	}

	/**
	 * Gets the questions
	 *
	 * @returns questions from questionnaire service
	 */
	getQuestions(): Observable<FormField[]> {
		return this._questionnaireService.getAllQuestions().pipe(
			map(questions => this.transformQuestionnaire(questions)),
			map(questions => questions.filter(question => question.activa === 1)),
			map(questions => this.sortQuestions(questions)),
			tap(questions => (this.questions = questions)),
		);
	}

	/**
	 * Gets the baremos
	 *
	 * @returns baremos from questionnaire service
	 */
	getBaremos(): Observable<BaremosDto> {
		return this._questionnaireService.getBaremos();
	}

	/**
	 * Gets the refusals
	 *
	 * @returns refusals from questionnaire service
	 */
	getRefusals(): Observable<RefusalDto[]> {
		return this._questionnaireService.getRefusals('PC');
	}

	/**
	 * Gets a question
	 *
	 * @param state home store
	 * @param index index number
	 * @returns question
	 */
	getQuestion(state: IQuestionnaireStore, index: number): Observable<FormField> {
		let question: FormField = state.questions[index].clone();
		const response = state.responses[question.key] || null;
		if (response) {
			question.valorPorDefecto = response + '';
		}
		if (question.isMultiple) {
			question.nombre = question.nombre.replace('{index}', `${question.index}`);
		}
		if (question.parametros) {
			question = this.evaluateQuestionParameters(question, state);
		}

		return of(question);
	}

	/**
	 * Gets the first question
	 *
	 * @param state home store
	 * @returns first question
	 */
	getFirstQuestion(state: IQuestionnaireStore): Observable<number> {
		return of(state.current === 0 ? state.current : 0);
	}

	/**
	 * Gets the next question
	 *
	 * @param state home store
	 * @returns next question index
	 */
	getNextQuestion(state: IQuestionnaireStore): number | null {
		return this.findNextQuestion(state);
	}

	/**
	 * Gets the next question
	 *
	 * @param state home store
	 * @returns next question index
	 */
	getPreviousQuestion(state: IQuestionnaireStore): number {
		return this.findPreviousQuestion(state);
	}

	/**
	 * Gets the result simulation
	 *
	 * @param state home store
	 * @returns result simulation
	 */
	getResultQuestionnaire(state: IQuestionnaireStore): ResultSimulation {
		const { refuse, list } = this.checkRefusalsStatus(state);
		const result: ResultSimulation = { valid: true, import: { imv: 0, infancia: 0 }, valueToRestImport: 0, errors: [] };
		if (refuse) {
			result.valid = false;
			result.errors = list;
		} else {
			const baremosManager = new BaremosManager(state.baremos);
			const variables: any = this.getVariables(state);
			this.getBaremoFound(baremosManager, state, variables, result);
		}
		return result;
	}

	private getBaremoFound(baremosManager: BaremosManager, state: IQuestionnaireStore, variables: any, result: ResultSimulation) {
		let baremoFound = false;
		const hasChildren = Object.prototype.hasOwnProperty.call(state.responses, 'PC4') && state.responses.PC4 > 0;
		baremosManager.baremos.forEach(baremo => {
			baremoFound = CoreExpressions.evaluate(baremo.condicion, state.responses, variables);
			if (baremoFound) {
				this.getImvAndChildren(variables, baremo, hasChildren, result);
				result.valueToRestImport = baremosManager.valueRestimport;
			}
		});
		return result;
	}

	private getImvAndChildren(variables: any, baremo: Baremos, hasChildren: boolean, result: ResultSimulation) {
		let imv = 0;
		let infancia = 0;
		if (variables.MP && variables.DC) {
			imv = baremo.ingresoVencidoDiscapacidadMonoparental;
			infancia = hasChildren ? baremo.ingresoInfanciaDiscapacidadMonoparental : 0;
		} else if (variables.DC) {
			imv = baremo.ingresoVencidoDiscapacidad;
			infancia = hasChildren ? baremo.ingresoInfanciaDiscapacidad : 0;
		} else if (variables.MP) {
			imv = baremo.ingresoVencidoMonoparental;
			infancia = hasChildren ? baremo.ingresoInfanciaDiscapacidad : 0;
		} else {
			imv = baremo.ingresoVencidoNoMonoparental;
			infancia = hasChildren ? baremo.ingresoInfanciaNoMonoparental : 0;
		}
		result.import.imv = imv;
		result.import.infancia = infancia;
	}

	/**
	 * Checks for refusals status
	 *
	 * @param state home store
	 * @returns list if there are refusal status or false if not.
	 */
	private checkRefusalsStatus(state: IQuestionnaireStore) {
		const refusalManager = new RefusalManager(state.refusals);
		let refuse = false;
		const list = [];

		for (const hard of refusalManager.getAll()) {
			let resultItem = false;
			let ignoreRefusal = false;

			if (hard.supuestoExentoCondicion) {
				ignoreRefusal = CoreExpressions.evaluate(hard.supuestoExentoCondicion, state.responses, this.getVariables(state));
			}
			if (!ignoreRefusal) {
				resultItem = CoreExpressions.evaluate(hard.condicion, state.responses, this.getVariables(state));
				if (resultItem) {
					list.push({
						code: hard.codigoFactor,
						text: hard.descripcion,
					});
				}
			}

			refuse = refuse || resultItem;
		}

		return { refuse, list };
	}

	/**
	 * Sorts the questions
	 *
	 * @param questions form field
	 * @returns sorted questions
	 */
	private sortQuestions(questions: FormField[]) {
		const items = [...questions];

		items.sort((a, b) => {
			if (a.orden > b.orden) {
				return 1;
			}
			if (a.orden < b.orden) {
				return -1;
			}
			return 0;
		});

		return items;
	}

	/**
	 * Evaluates question parameters
	 *
	 * @param question form field
	 * @param state home store
	 * @returns question with new parameters or new question
	 */
	private evaluateQuestionParameters(question: FormField, state: IQuestionnaireStore): FormField {
		const newQuestion = question;

		for (const paramData of question.parametros) {
			const condition = paramData.condicion.trim() || '';
			const result = condition ? CoreExpressions.evaluate(condition, state.responses, this.getVariables(state)) : true;
			if (result) {
				question = this.questionReplaceParameter(question, paramData.params);
				return question;
			}
		}

		return newQuestion;
	}

	/**
	 * Replaces question parameters
	 *
	 * @param question form field
	 * @param params parameters
	 * @returns question
	 */
	private questionReplaceParameter(question: FormField, params: { [key: string]: any }): FormField {
		for (const key in params) {
			if (params[key]) {
				const value = this.formatNumber(params[key]);
				const regex = new RegExp(`{${key}}`, 'g');
				question.nombre = question.nombre.replace(regex, value);

				if (Array.isArray(question.keyLista) && question.keyLista.length) {
					const options = [...question.keyLista];
					options.forEach((option, index) => {
						options[index] = { ...options[index] };
						options[index].opcion = options[index].opcion.replace(regex, value);
					});
					question.keyLista = options;
				}
			}
		}

		return question;
	}

	/**
	 * Finds next question
	 *
	 * @param state home store
	 * @returns next question
	 */
	private findNextQuestion(state: IQuestionnaireStore): number | null {
		let index = state.current + 1;
		let isVisible = false;
		const refusalManager = new RefusalManager(state.refusals);

		while (index < state.questions.length && !isVisible) {
			const question = state.questions[index];
			const visibility = question.expresionVisibilidad || '';
			if (!visibility) {
				isVisible = true;
			} else {
				isVisible = CoreExpressions.evaluate(visibility, state.responses, this.getVariables(state));
			}

			if (isVisible && refusalManager.hasSoft(question.key)) {
				isVisible = refusalManager.checkSoftConditions(question.key, state.responses, this.getVariables(state));
			}

			if (isVisible) {
				return index;
			}
			index++;
		}

		return null;
	}

	/**
	 * Finds previous question
	 *
	 * @param state home store
	 * @returns previous question
	 */
	private findPreviousQuestion(state: IQuestionnaireStore): number {
		const lastResponse = state.history[state.history.length - 1];
		if (lastResponse) {
			return this.findQuestionByKey(lastResponse.key, state.questions);
		}

		return -1;
	}

	/**
	 * Generates questions
	 *
	 * @param responses responses
	 * @returns form field with questions
	 */
	public GENERATE_QUESTIONS(responses: any): FormField[] {
		const questions: FormField[] = [];

		this.questions.forEach(question => {
			if (question.isMultiple) {
				const count = +CoreExpressions.evaluate(question.repetirN, responses, {});
				const newQuestion = FormField.fromDto(question);
				if (newQuestion.children.length !== count) {
					newQuestion.repeat(count);
				}

				for (const child of newQuestion.children) {
					questions.push(child);
				}
			} else {
				questions.push(question);
			}
		});

		return questions;
	}

	/**
	 * Finds a question by key
	 *
	 * @param key question key
	 * @param questions form field
	 * @returns index question
	 */
	private findQuestionByKey(key: string, questions: FormField[]): number {
		return questions.findIndex(question => question.key === key);
	}

	/**
	 * Formats a number
	 *
	 * @param num number
	 * @returns formatted number
	 */
	private formatNumber(num: number): string {
		return num.toLocaleString('de', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	}

	/**
	 * Transforms the questions
	 *
	 * @param form formDto
	 * @returns form field array with questions
	 */
	private transformQuestionnaire(form: FormDto): Array<FormField> {
		const questions: FormField[] = [];

		const question1 = form.formulario[0].bloques[0].grupos[0].campos[0];
		questions.push(FormField.fromDto(question1));
		for (const field of form.formulario[0].bloques[0].grupos[1].campos) {
			questions.push(FormField.fromDto(field));
		}

		return questions;
	}

	/**
	 * Gets the variables
	 *
	 * @param state home store
	 * @returns variables
	 */
	private getVariables(state: IQuestionnaireStore) {
		const variables = { CAISS: 0, MP: 0, DC: 0 };

		if (this.appConfig.caiss) {
			variables.CAISS = 1;
		}

		const monoparentalidad = Array.isArray(state.monoparentalidad) ? state.monoparentalidad[0] : state.monoparentalidad;
		const discapacidad = Array.isArray(state.discapacidad) ? state.discapacidad[0] : state.discapacidad;

		if (CoreExpressions.evaluate(monoparentalidad, state.responses, {})) {
			variables.MP = 1;
		}

		if (CoreExpressions.evaluate(discapacidad, state.responses, {})) {
			variables.DC = 1;
		}

		return variables;
	}
}
